import { useState, ChangeEvent } from 'react';
import {
  Box,
  VStack,
  Input,
  Button,
  Text,
  FormControl,
  FormErrorMessage,
  FormLabel,
  useToast,
  Image,
  Heading,
  Link,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import isEmail from 'validator/lib/isEmail';
import { ROUTES } from '../../constants/FrontendPathConstants';

interface SignUpForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface SignUpErrors {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export const SignUpPage = (): JSX.Element => {
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState<SignUpForm>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState<SignUpErrors>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = (): boolean => {
    const nextErrors: SignUpErrors = {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
    };
    let valid = true;

    if (!formData.firstName.trim()) {
      nextErrors.firstName = 'First name is required';
      valid = false;
    } else if (!/^[A-Za-z\s]{2,30}$/.test(formData.firstName)) {
      nextErrors.firstName = '2-30 letters only';
      valid = false;
    }

    if (!formData.lastName.trim()) {
      nextErrors.lastName = 'Last name is required';
      valid = false;
    } else if (!/^[A-Za-z\s]{2,30}$/.test(formData.lastName)) {
      nextErrors.lastName = '2-30 letters only';
      valid = false;
    }

    if (!formData.email) {
      nextErrors.email = 'Email is required';
      valid = false;
    } else if (!isEmail(formData.email)) {
      nextErrors.email = 'Enter a valid email';
      valid = false;
    }

    if (!formData.password) {
      nextErrors.password = 'Password is required';
      valid = false;
    } else if (formData.password.length < 8) {
      nextErrors.password = 'Password must be at least 8 characters';
      valid = false;
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      nextErrors.password = 'Must include uppercase, lowercase, and number';
      valid = false;
    }

    if (!formData.confirmPassword) {
      nextErrors.confirmPassword = 'Please confirm your password';
      valid = false;
    } else if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = 'Passwords do not match';
      valid = false;
    }

    setErrors(nextErrors);
    return valid;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validate()) {
      toast({
        title: 'Validation error',
        description: 'Fix the highlighted fields',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          email: formData.email.toLowerCase(),
          password: formData.password,
        }),
      });

      if (res.status === 409) {
        throw new Error('User already exists');
      }

      if (!res.ok) {
        throw new Error('Request failed');
      }

      toast({
        title: 'Account created!',
        description: 'Please check your email to verify your account before logging in.',
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
      navigate(ROUTES.LOGIN + '?message=verification_sent');
    } catch (err) {
      console.error(err);
      if (err instanceof Error && err.message === 'User already exists') {
        toast({
          title: 'User already exists',
          description: 'Please log in or use a different email',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Sign-up failed',
          description: 'Please try again later',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="surface.300"
      position="relative"
      p={4}
      py={{ base: 20, md: 4 }}
    >
      {/* Logo at top */}
      <Box position="absolute" top={6} left="50%" transform="translateX(-50%)">
        <Image
          src="/images/UGenomeHorizontalBlue.png"
          alt="UGenome Logo"
          h="60px"
          objectFit="contain"
        />
      </Box>

      {/* Sign Up Card */}
      <Box
        bg="white"
        p={{ base: 6, md: 10 }}
        borderRadius="16px"
        boxShadow="0 4px 20px rgba(26, 18, 98, 0.1)"
        w={{ base: '100%', sm: '440px' }}
        maxW="440px"
        my={{ base: 16, md: 0 }}
      >
        <VStack spacing={6}>
          {/* Header */}
          <VStack spacing={2}>
            <Heading size="lg" color="primary.500" textAlign="center">
              Create Account
            </Heading>
            <Text color="muted.400" fontSize="sm" textAlign="center">
              Get started with Seq4Life platform
            </Text>
          </VStack>

          {/* Form */}
          <VStack spacing={4} w="100%">
            <FormControl isInvalid={!!errors.firstName}>
              <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                First Name
              </FormLabel>
              <Input
                id="firstName"
                name="firstName"
                placeholder="John"
                size="lg"
                value={formData.firstName}
                onChange={handleChange}
              />
              <FormErrorMessage>{errors.firstName}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.lastName}>
              <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                Last Name
              </FormLabel>
              <Input
                id="lastName"
                name="lastName"
                placeholder="Doe"
                size="lg"
                value={formData.lastName}
                onChange={handleChange}
              />
              <FormErrorMessage>{errors.lastName}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.email}>
              <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                Email Address
              </FormLabel>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="john.doe@email.com"
                size="lg"
                value={formData.email}
                onChange={handleChange}
              />
              <FormErrorMessage>{errors.email}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.password}>
              <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                Password
              </FormLabel>
              <InputGroup size="lg">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.password}</FormErrorMessage>
              <Text fontSize="xs" color="muted.400" mt={1}>
                Min 8 characters with uppercase, lowercase, and number
              </Text>
            </FormControl>

            <FormControl isInvalid={!!errors.confirmPassword}>
              <FormLabel fontSize="sm" fontWeight="medium" color="primary.500">
                Confirm Password
              </FormLabel>
              <InputGroup size="lg">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage>{errors.confirmPassword}</FormErrorMessage>
            </FormControl>

            <Button
              w="100%"
              size="lg"
              variant="primary"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Creating account..."
              mt={2}
            >
              Create Account
            </Button>
          </VStack>

          {/* Footer */}
          <Text fontSize="sm" color="muted.400">
            Already have an account?{' '}
            <Link
              color="primary.500"
              fontWeight="semibold"
              onClick={() => navigate(ROUTES.LOGIN)}
              _hover={{ textDecoration: 'underline' }}
            >
              Sign In
            </Link>
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};
